import axios from "axios";

const publicUserApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/user/api/v1`,
});

export interface PublicGalleryItem {
  id: string;
  type: "photo" | "video" | "pdf";
  url: string;
  title: string;
  description?: string;
  size?: string;
  thumbnail?: string;
  uploadedAt?: string;
}

export interface PublicTreatmentRetreatPackage {
  _id: string;
  packageName: string;
  treatmentType: string;
  specialization: string;
  duration?: string;
  category?: string;
  subCategory?: string;
  state?: string;
  city?: string;
  address?: string;
  status?: string;
  bookingType?: string;
  TreatmentOrganiser?: string;
  availabilityFrom?: string;
  availabilityTo?: string;
  startTime?: string;
  endTime?: string;
  availableSlots?: number;
  googleMapEmbedUrl?: string;
  googleMapUrl?: string;
  uploadImages?: string[];
  price?: number;
  oldPrice?: number;
  sessionPrice?: number;
  addOns?: string;
  pricingOptions?: { type: string; price: number; platformFee: number }[];
  addOnsList?: { name: string; price: number }[];
  highlights?: string[];
  includes?: string[];
  exclusions?: string[];
  tags?: string;
  faqs?: { id: string; question: string; answer: string }[];
  itinerary?: { id: string; day: string; description: string }[];
  whatsIncluded?: Array<{
    id?: string;
    title: string;
    duration?: string;
    description: string;
    icon?: string;
    iconBg?: string;
    iconColor?: string;
  }>;
  gallery?: PublicGalleryItem[];
  description?: string;
  markAsTrending?: boolean;
  commissionPercentage?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

interface PublicPackagesResponse {
  success: boolean;
  message?: string;
  data: PublicTreatmentRetreatPackage[];
}

interface PublicPackageResponse {
  success: boolean;
  message?: string;
  data: PublicTreatmentRetreatPackage | null;
}

export const fetchPublicTreatmentRetreatPackages =
  async (): Promise<PublicPackagesResponse> => {
    try {
      const response =
        await publicUserApi.get<PublicPackagesResponse>("/treatment-retreats");
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Failed to fetch treatment packages",
        data: [],
      };
    }
  };

export const fetchPublicTreatmentRetreatPackageById = async (
  packageId: string
): Promise<PublicPackageResponse> => {
  try {
    const response = await publicUserApi.get<PublicPackageResponse>(
      `/treatment-retreats/${packageId}`
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "Failed to fetch treatment package details",
      data: null,
    };
  }
};

export const bookTreatmentRetreat = async (bookingData: any) => {
  try {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await publicUserApi.post("/book-treatment-retreat", bookingData, { headers });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const verifyTreatmentRetreatPayment = async (paymentData: any) => {
  try {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await publicUserApi.post("/verify-treatment-retreat-payment", paymentData, { headers });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export interface ApplicableCommissionResponse {
  success: boolean;
  data?: {
    commissionPercentage: number;
  };
  message?: string;
}

export const fetchApplicableCommission = async (params: {
  serviceId?: string;
  vendorId?: string;
}): Promise<number | null> => {
  try {
    const response = await publicUserApi.get<ApplicableCommissionResponse>(
      "/commission/applicable",
      { params }
    );
    if (
      response.data?.success &&
      typeof response.data.data?.commissionPercentage === "number"
    ) {
      return response.data.data.commissionPercentage;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch applicable commission:", error);
    return null;
  }
};

export const uploadTreatmentGalleryMediaApi = async (
  packageId: string,
  formData: FormData
) => {
  try {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = { "Content-Type": "multipart/form-data" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const response = await publicUserApi.post(
      `/treatment-retreats/${packageId}/gallery`,
      formData,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const deleteTreatmentGalleryItemApi = async (
  packageId: string,
  mediaId: string
) => {
  try {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const response = await publicUserApi.delete(
      `/treatment-retreats/${packageId}/gallery/${mediaId}`,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
